import React, { useEffect, useState } from "react";
import { useKeycloakToken } from '../common/useKeycloak';
import { useNavigate, useParams } from 'react-router-dom';
import { Connection, NewIntegration, Organization, ProviderType, buildProviderType, buildNewIntegration } from "../common/ObjectTypes";
import { createNewConnection } from "../../api/connections/connectionsApi";
import Spinner from "../common/Spinner";
import { filterDestinationOwner } from "../destinations/DestinationHelperFunctions";
import { toast } from "react-toastify";
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import addKeywords from 'ajv-keywords';
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { page_size } from "../../api/apiUtils";
import { useTranslation } from "react-i18next";
import NewConnectionStepTwo from "./NewConnectionStepTwo";
import { loadProviderTypes } from "../../api/connections/loadProviderTypesNewConnection";
import { useKeycloak } from "@react-keycloak/web";
import ConnectionProgressIndicator from "./NewConnectionProgressIndicator/ConnectionProgressIndicator";
import { trackConnectionCreated, trackConnectionCreationAbandoned } from '../../analytics/analytics';
import { loadCurrentUserDetails } from "../organizations/loadCurrentUserDetails";
import { containerClasses, buttonClasses, textClasses } from "../common/tailwindClassnames";
import { getConnectionKind } from "../common/connectionKind";

export function ManageAddConnectionStepTwo() {
    type FilterTypes = {
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    };
    function createFilterTypes(
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    ): FilterTypes {
        return {
            endpoint__in: endpoint__in || [],
            enabled: enabled || [],
            type__in: type__in || [],
            owner__in: owner__in || [],
            ordering: ordering || "",
            search: search || ""
        }
    }
    type FilterTags = {
        name: string,
        id: string
    };
    const { t } = useTranslation();
    const { pt_value } = useParams();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak, initialized } = useKeycloak();
    const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState<boolean>(false);
    const [destinationFilter, setDestinationFilter] = useState<FilterTypes>(createFilterTypes([], [], [], [], "", ""));
    const [initialConnection, setInitialConnection] = useState<NewIntegration>(buildNewIntegration(true))
    const [createdConnection, setCreatedConnection] = useState<Connection>()
    const [updatedConfigurations, setUpdatedConfigurations] = useState<any[]>([]);
    const [errors, setErrors] = useState({});
    const [rsjfErrors, setRsjfErrors] = useState<any[]>([]);
    const [routeId, setRouteId] = useState("");

    const [reloadDestinations, setReloadDestinations] = useState(false);

    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [loadMoreOrganizations, setLoadMoreOrganizations] = useState(true);
    const [organizationOffset, setOrganizationOffset] = useState<number>(0);
    const [orgOffset, setOrgOffset] = useState<number>(0);

    const [selectedProviderType, setSelectedProviderType] = useState<ProviderType>(buildProviderType());
    const [pageLoaded, setPageLoaded] = useState(false);
    const [currentStep, setCurrentStep] = useState(2);
    const navigate = useNavigate();

    const getSaveButtonClassName = (step: number, disabled: boolean): string => {
        if (step === 1) {
            return disabled
                ? "bg-gray-lines text-white transition-colors duration-300"
                : "bg-dark-green hover:bg-status-green text-white transition-colors duration-300";
        }
        if (step === 2) {
            return "bg-dark-green hover:bg-status-green text-white transition-colors duration-300";
        }
        if (step === 3) {
            return "bg-dark-green hover:bg-status-green text-white transition-colors duration-300";
        }
        return "";
    };

    const loadOrganizationsData = async (offset: number, search_str: string) => {
        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                const [orgs, total_orgs, error] = await loadOrganizations(token, offset, search_str, organizations);
                setLoadingOrganizations(true);
                if (!error) {
                    setOrganizations(orgs);
                    if (offset + page_size >= total_orgs) {
                        setLoadMoreOrganizations(false);
                    }
                    setLoadingOrganizations(false);
                }
            } catch (err) {
                // Silently handle errors - they're already handled by the interceptor
                console.log('Failed to load organizations:', err);
                setLoadingOrganizations(false);
            }
        }
    }

    const loadProviderTypesData = async (search_str: string) => {
        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                const [provider_types, error] = await loadProviderTypes(token, search_str);
                if (!error && provider_types && provider_types.length > 0 && provider_types[0]) {
                    setSelectedProviderType(provider_types[0]);
                    setInitialConnection((prevConnection: any) => ({
                        ...prevConnection,
                        type: provider_types[0].id
                    }))
                    setPageLoaded(true);
                }
            } catch (err) {
                // Silently handle errors - they're already handled by the interceptor
                console.log('Failed to load provider types:', err);
            }
        }
    }

    useEffect(() => {
        pt_value && loadProviderTypesData(pt_value);
        loadOrganizationsData(organizationOffset, "");

    }, [token, reloadDestinations, providerTypes])


    const handleChangeEndpoint = (e: any) => {
        setInitialConnection((prevConnection: any) => ({
            ...prevConnection,
            base_url: e.target.value
        }))
    }

    const handleChangeSchemaForm = (e: any, action_id: string) => {
        const newConfig = {
            "action": action_id,
            "data": e
        }

        if (e) {
            const index_to_find = updatedConfigurations.findIndex((a: any) => a.action === action_id)
            if (index_to_find === -1) {
                // if this is a new config id, push it to the array
                setUpdatedConfigurations((prevArray: any[]) => [...prevArray, newConfig])
            } else {
                // if it is an existing config id, replace the old config with the new one
                const tempArray = updatedConfigurations
                tempArray[index_to_find] = newConfig
                setUpdatedConfigurations(tempArray)
            }
        }
        setInitialConnection((prevConnection: any) => ({
            ...prevConnection,
            configurations: updatedConfigurations
        }))
    }

    const handleChangeName = (e: any) => {
        setInitialConnection((prevConnection: any) => ({
            ...prevConnection,
            name: e.target.value
        }))
    }

    const handleSelectOwner = async (e: any) => {
        // Check if e and e.value are defined
        if (!e || !e.value) {
            return;
        }

        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        try {
            const isAdmin = await loadCurrentUserDetails(e.value, keycloak.token ? keycloak.token : "");
            setCurrentMemberIsAdmin(isAdmin);
            setInitialConnection((prevConnection: any) => ({
                ...prevConnection,
                owner: e.value
            }))
            setDestinationFilter(filterDestinationOwner({
                target: {
                    "name": e.label || '',
                    "value": e.value,
                }
            }, destinationFilter));
            setReloadDestinations(true);
        } catch (err) {
            // Silently handle errors - they're already handled by the interceptor
            console.log('Failed to load user details:', err);
        }
    }

    const formIsValid = async () => {
        let form_errors: any = {};
        let rsjf_errors: any = {};
        if (initialConnection.name.length === 0) form_errors.name = "Name is required."
        if (initialConnection.name.length > 50) form_errors.name = "Name cannot exceed 50 characters.";
        if (initialConnection.base_url.length > 0) {
            try {
                new URL(initialConnection.base_url);
            } catch (err) {
                form_errors.base_url = "Enter a valid url including the scheme."
            }
        }
        if (initialConnection.owner === "") form_errors.owner = "Select a workspace."

        setErrors(form_errors)
        setRsjfErrors([])

        // Check if selectedProviderType and its actions are defined
        if (!selectedProviderType || !selectedProviderType.actions || !Array.isArray(selectedProviderType.actions)) {
            return Object.keys(form_errors).length === 0;
        }

        updatedConfigurations.forEach((config: any, index: number) => {
            const schema_index = selectedProviderType.actions.findIndex((a: any) => a.id === config.action);
            // Skip if schema_index is not found
            if (schema_index === -1) {
                return;
            }
            const ajv = new Ajv({ allErrors: true, verbose: true, strictTypes: false });
            ajv.addKeyword({
                keyword: 'is_executable',
                validate: function () {
                    return true;
                },
                errors: false
            });
            addFormats(ajv);

            ajv.addFormat('data-url', {
                type: 'string',
                validate: (data: string) => {
                    const regex = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
                    return regex.test(data);
                },
            });

            addKeywords(ajv);

            let schema = selectedProviderType.actions[schema_index].schema;
            const validate = ajv.compile(schema);

            if (schema.properties && typeof schema.properties === 'object' && "files" in schema.properties) {
                const files = config.data?.files;
                if (Array.isArray(files)) {
                    if (files.length === 0) {
                        config.data.files = "";
                    } else {
                        // Convert the files array to a JSON string
                        config.data.files = JSON.stringify(files);
                    }
                }
            }

            const data = { ...config.data };

            if (schema.properties && typeof schema.properties === 'object') {
                Object.keys(schema.properties).forEach((key) => {
                    if (!data.hasOwnProperty(key)) {
                        data[key] = null;
                    } else {
                        if (data[key] === undefined) {
                            data[key] = null;
                        }
                    }
                });
            } else {
                console.error('schema.properties is not defined or is not an object');
            }
            let modifiedSchema = JSON.parse(JSON.stringify(schema));
            if (schema.if && schema.then && schema.else) {
                // Validate against 'if' condition
                const validateIf = ajv.compile(schema.if);

                if (validateIf(data)) {
                    if (schema.then && typeof schema.then === "object") {
                        modifiedSchema = {
                            ...schema,
                            properties: { ...schema.properties, ...schema.then.properties },
                            required: schema.then.required || [],
                        };
                    }
                } else {
                    if (schema.else && typeof schema.else === "object") {
                        modifiedSchema = {
                            ...schema,
                            properties: { ...schema.properties, ...schema.else.properties },
                            required: schema.else.required || [],
                        };
                    }
                }
                const validateFinal = ajv.compile(modifiedSchema);
                const isValid = validateFinal(data);

                if (!isValid) {
                    validateFinal.errors?.forEach((error: any) => {
                        console.error(`Error in field '${error.instancePath}': ${error.message}`);
                    });
                }
            }

            Object.keys(modifiedSchema.properties).forEach((key) => {
                if (!data.hasOwnProperty(key)) {
                    data[key] = null;
                } else if (data[key] === undefined) {
                    data[key] = null;
                }
            });

            const validateUpdated = ajv.compile(modifiedSchema);
            const valid = validateUpdated(data);
            if (!valid) {
                validateUpdated.errors?.forEach((error: any) => {
                    console.error(`Error in field '${error.instancePath}': ${error.message}`);
                    const field = error.instancePath.slice(1) || error.params.missingProperty;
                    const isFieldRequired = modifiedSchema.required && modifiedSchema.required.includes(field);
                    const hasPattern = modifiedSchema.properties[field]?.pattern;

                    // Skip validation if field is not required and is empty
                    if (!isFieldRequired && (data[field] === null || data[field] === undefined)) {
                        return;
                    }

                    // Validate pattern if defined
                    if (hasPattern && !new RegExp(hasPattern).test(data[field])) {
                        rsjf_errors[schema_index] = rsjf_errors[schema_index] || {};
                        rsjf_errors[schema_index][field] = `Field '${field}' does not match the required pattern.`;
                    }

                    if (!rsjf_errors[selectedProviderType.actions[schema_index].id]) {
                        rsjf_errors[selectedProviderType.actions[schema_index].id] = {};
                    }
                    rsjf_errors[selectedProviderType.actions[schema_index].id][field] = `Error in field '${field}': ${error.message}`;
                });
            }
        });

        setRsjfErrors({ ...rsjf_errors });
        return Object.keys(form_errors).length === 0 && Object.keys(rsjf_errors).length === 0;
    }

    const handleCreateConnection = async (connectionToAdd: NewIntegration) => {
        if (!(await formIsValid())) return;

        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                const temp = await createNewConnection(token, connectionToAdd);
                // Check if temp is defined and has required properties
                if (!temp || !temp.default_route || !temp.default_route.id) {
                    return;
                }
                setCreatedConnection(temp)
                setRouteId(temp.default_route.id);
                const workspaceName = temp.owner?.name || organizations.find((o: Organization) => o.id === initialConnection.owner)?.name;
                trackConnectionCreated(temp.id, selectedProviderType?.name, workspaceName);
                toast.success(`${t("New connection made, ready to add destinations")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                navigate(`/connections/new/${temp.id}/configuredestinations`, {
                    state: { isWebhook: getConnectionKind(selectedProviderType) === 'webhook' }
                })
            } catch (err: any) {
                console.log('Failed to create connection:', err);
                // Session expiry is handled by the axios interceptor + LoginModal; don't stack a toast on it.
                if (err?.response?.status === 401) return;
                toast.error(t("Something went wrong. Please try again or contact support."), {
                    position: toast.POSITION.TOP_RIGHT
                });
            }
        }
    }

    const handleClickNext = async (newStep: number) => {
        if (newStep === 3) {
            if (!(await formIsValid())) {
                setCurrentStep(currentStep);
                return;
            } else {
                await handleCreateConnection(initialConnection);
                setCurrentStep(newStep);
            }
        }
    }

    function handleSearchOrgDropdown(e: any) {
        if (e.length > 1) loadOrganizationsData(orgOffset, e);
    }

    const handleLoadMoreOrganizations = () => {
        setOrganizationOffset(organizationOffset + page_size);
        loadOrganizationsData(organizationOffset + page_size, "");
    }

    const handleCancel = () => {
        const workspaceName = organizations.find((o: Organization) => o.id === initialConnection.owner)?.name;
        trackConnectionCreationAbandoned(2, selectedProviderType?.name, workspaceName);
        navigate("/")
    }

    return !pageLoaded ?
        (
            <Spinner />
        ) : (
            <div>
                <div className="bg-white shadow static inset-x-0 px-12 pt-5 pb-1 -mt-5 -mx-5 desktop:-mx-12 transition-colors duration-300">
                    <div className="max-w-list-table mx-auto">
                        <h1 className="text-3xl font-semibold mb-3 text-off-black transition-colors duration-300">{t("New")} {selectedProviderType?.name || ''} {t("Connection")}</h1>
                    </div>
                </div>
                <div className="max-w-list-table mx-auto">
                    <ConnectionProgressIndicator
                        stepZeroStatus="completed"
                        stepOneStatus="in-progress"
                        stepTwoStatus="upcoming"
                        webhookStepStatus={getConnectionKind(selectedProviderType) === 'webhook' ? "upcoming" : undefined}
                        confirmationStatus="upcoming"
                    />
                    <NewConnectionStepTwo
                        currentMemberIsAdmin={currentMemberIsAdmin}
                        initialConnection={initialConnection}
                        errors={errors}
                        rsjfErrors={rsjfErrors}
                        onChangeSchemaForm={handleChangeSchemaForm}
                        onChangeName={handleChangeName}
                        onSelectOwner={handleSelectOwner}
                        selectedProviderType={selectedProviderType}
                        organizations={organizations}
                        loadMoreOrgs={handleLoadMoreOrganizations}
                        orgInputChange={handleSearchOrgDropdown}
                    />
                </div>

                <div className="fixed bottom-0 right-0 py-3 bg-white/10 backdrop-blur w-full shadow-lg transition-colors duration-300">
                    <div className="max-w-list-table mx-auto flex flex-row px-8">
                        <button className={`px-8 py-2 font-bold rounded mr-3 transition-colors duration-300 ${getSaveButtonClassName(currentStep, false)}`}
                            onClick={() => handleClickNext(currentStep + 1)}
                        >
                            {currentStep === 3 ? t("Finish") : t("Next")}
                        </button>
                        <button className={`${buttonClasses.cancel} mx-2`}
                            type="button"
                            onClick={handleCancel}
                        >
                            {t("Cancel")}
                        </button>
                    </div>
                </div>
            </div>
        )
}